import defaultLogo from './SC-Signers-Choice-Logo-White-TM-300x68.webp';

/**
 * Branding configuration for the Signers Choice Tech Check application.
 *
 * These values can be overridden at runtime by passing query string parameters
 * on the URL used to embed the application (e.g. in an iframe). This allows
 * partners to brand the tech check to match their own look and feel without
 * requiring a separate build.
 *
 * Supported query params:
 *   - primaryColor: hex color used as the MUI primary color (e.g. %232EB99A -> #2EB99A)
 *   - logoUrl:      absolute URL to a logo image displayed in the sidebar/header
 *   - appName:      the product/brand name shown in the document title and UI
 *   - sidebarColor: hex color used as the background of the branded sidebar
 *   - showLogo:     whether to display the logo and the bar/sidebar that
 *                   contains it (pass showLogo=false to hide them entirely)
 */
export interface BrandingConfig {
  primaryColor: string;
  logoUrl: string;
  appName: string;
  sidebarColor: string;
  showLogo: boolean;
}

/** Signers Choice defaults, used when no override is provided. */
export const DEFAULT_BRANDING: BrandingConfig = {
  primaryColor: '#2EB99A',
  logoUrl: defaultLogo,
  appName: 'Signers Choice',
  sidebarColor: '#06033A',
  showLogo: true,
};

/** Matches 3, 4, 6, or 8 digit hex colors (with leading #). */
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function normalizeColor(value: string | null): string | undefined {
  if (!value) return undefined;
  // Allow callers to omit the leading '#'; the browser decodes %23 -> # for us.
  const candidate = value.startsWith('#') ? value : `#${value}`;
  return HEX_COLOR_REGEX.test(candidate) ? candidate : undefined;
}

function normalizeUrl(value: string | null): string | undefined {
  if (!value) return undefined;
  try {
    // Only allow http(s) URLs to avoid injecting arbitrary schemes (e.g. javascript:).
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.toString();
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function normalizeText(value: string | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeBoolean(value: string | null): boolean | undefined {
  if (value === null) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
  return undefined;
}

/**
 * Reads branding overrides from the provided query string (defaults to the
 * current window location) and merges them over the Signers Choice defaults.
 */
export function getBrandingConfig(search: string = window.location.search): BrandingConfig {
  const params = new URLSearchParams(search);

  return {
    primaryColor: normalizeColor(params.get('primaryColor')) ?? DEFAULT_BRANDING.primaryColor,
    logoUrl: normalizeUrl(params.get('logoUrl')) ?? DEFAULT_BRANDING.logoUrl,
    appName: normalizeText(params.get('appName')) ?? DEFAULT_BRANDING.appName,
    sidebarColor: normalizeColor(params.get('sidebarColor')) ?? DEFAULT_BRANDING.sidebarColor,
    showLogo: normalizeBoolean(params.get('showLogo')) ?? DEFAULT_BRANDING.showLogo,
  };
}
